import type { ReportedContentItem } from '@wlb/shared/api/admin/adminTypes';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { StatusPill } from '../../components/ui/StatusPill';
import { PostCard, type PostCardFact } from '../posts/PostCard';
import { postPlainText } from '../posts/postMedia';
import { formatDateTime } from '../../utils/format';

interface ReportedContentCardProps {
  item: ReportedContentItem;
  selected: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
  onHide: () => void;
  onRestore: () => void;
  pending?: boolean;
}

/**
 * A reported post, shown the way the member sees it, with the report stapled
 * underneath.
 *
 * The decision an operator makes is about the content, so the content leads;
 * the reason, the reporter and the timing sit in a tinted block below it
 * because they are the case file, not part of the post.
 */
export function ReportedContentCard({
  item,
  selected,
  onToggle,
  onApprove,
  onReject,
  onHide,
  onRestore,
  pending = false,
}: ReportedContentCardProps) {
  const { t } = useTranslation('admin');
  const authorName = item.postedBy || t('common.none');

  const facts: PostCardFact[] = [
    { label: t('moderation.card.reason'), value: item.reason },
    {
      label: t('moderation.card.reportedBy'),
      value: item.reportedBy
        ? item.reportedByEmail
          ? `${item.reportedBy} (${item.reportedByEmail})`
          : item.reportedBy
        : '',
    },
    { label: t('moderation.card.reportedOn'), value: formatDateTime(item.reportedOn) },
  ];

  if (item.isComment && item.parentPostTitle) {
    facts.push({ label: t('moderation.card.inPost'), value: item.parentPostTitle });
  }
  if (item.postedByEmail) {
    facts.push({ label: t('moderation.card.authorEmail'), value: item.postedByEmail });
  }
  if (item.reviewerComments) {
    facts.push({
      label: t('moderation.card.reviewerNotes'),
      value: item.reviewerComments,
    });
  }

  return (
    <PostCard
      authorName={authorName}
      authorAvatar={item.postedByAvatar || null}
      meta={[formatDateTime(item.postedOn), item.groupName]}
      badges={
        <>
          <StatusPill
            label={item.isComment ? t('moderation.type.comment') : t('moderation.type.post')}
            tone="info"
          />
          {item.isBlog ? <StatusPill label={t('posts.type.blog')} tone="neutral" /> : null}
          <StatusPill
            label={
              item.isReviewed
                ? item.isApproved
                  ? t('moderation.status.upheld')
                  : t('moderation.status.dismissed')
                : t('status.pending')
            }
            tone={item.isReviewed ? (item.isApproved ? 'success' : 'neutral') : 'warning'}
          />
          {!item.isPostActive ? (
            <StatusPill label={t('moderation.status.hidden')} tone="danger" />
          ) : null}
        </>
      }
      title={item.title || undefined}
      text={postPlainText(item.content) || t('moderation.card.noContent')}
      images={item.postImages}
      engagement={{ likes: item.likes, comments: item.comments, shares: item.shares }}
      facts={facts}
      factsTone="danger"
      selection={{ selected, onToggle }}
      actions={
        <>
          {!item.isReviewed ? (
            <>
              <Button small disabled={pending} onClick={onApprove}>
                <Icon name="shield" size="sm" />
                {t('moderation.actions.approve')}
              </Button>
              <Button variant="secondary" small disabled={pending} onClick={onReject}>
                <Icon name="close" size="sm" />
                {t('moderation.actions.reject')}
              </Button>
            </>
          ) : null}
          {item.postId > 0 ? (
            item.isPostActive ? (
              <Button variant="danger" small disabled={pending} onClick={onHide}>
                <Icon name="eyeOff" size="sm" />
                {t('moderation.card.hide')}
              </Button>
            ) : (
              <Button variant="secondary" small disabled={pending} onClick={onRestore}>
                <Icon name="view" size="sm" />
                {t('moderation.card.restore')}
              </Button>
            )
          ) : null}
        </>
      }
    />
  );
}
